import i18n from "../../../i18n";
import { EnvironmentPollution, EnvironmentPollutionType } from "./environment-pollution";
import IEnvironmentHazard, { EnvironmentHazardType } from "./environment-hazard";
import ISpatialProperty, { SpatialPropertyType } from "./spatialProperty";
import FrontLightsMode from "./front-lights-mode";
import { TeachAndRepeatResolveType } from "../../../enums/robot/rooms/teach-and-repeat-resolve-type";

export const INHERIT = "Inherit";
type INHERIT_TYPE = typeof INHERIT;

export interface IndependentConfigurationLegacy2601 {
  warningLight: boolean | null;
  beeper: boolean | null;
  minObstacleHeightM: number | null;
}

export interface IndependentConfigurationLegacy2601Api {
  warningLight: boolean | INHERIT_TYPE;
  beeper: boolean | INHERIT_TYPE;
  minObstacleHeightM: number | INHERIT_TYPE;
}

export interface CleaningConfiguration {
  spatialProperty: SpatialPropertyType | null;
  environmentPollution: EnvironmentPollutionType | null;
  environmentHazard: EnvironmentHazardType | null;
  independentLegacy2601?: IndependentConfigurationLegacy2601 | null;
}

export interface CleaningConfigurationApi {
  independentLegacy2601?: IndependentConfigurationLegacy2601Api | null;
  spatialProperty: SpatialPropertyType | INHERIT_TYPE;
  environmentPollution: EnvironmentPollutionType | INHERIT_TYPE;
  environmentHazard: EnvironmentHazardType | INHERIT_TYPE;
}

export enum MinObstacleHeightOption {
  UnderFourCm = 0.03,
  FourToEightCm = 0.06,
  OverEightCm = 0.09,
}

export const DEFAULT_MIN_OBSTACLE_HEIGHT = MinObstacleHeightOption.FourToEightCm;

export enum CleaningConfigurationLevel {
  Zone,
  Room,
  Global,
}

export type CleaningConfigurationTypeSet =
  | SpatialPropertyType
  | EnvironmentPollutionType
  | EnvironmentHazardType
  | TeachAndRepeatResolveType
  | MinObstacleHeightOption;

export const getCleaningModeOptionText = (
  option: CleaningConfigurationTypeSet | null,
  level: CleaningConfigurationLevel,
  roomOption: CleaningConfigurationTypeSet | undefined | null,
  globalOption: CleaningConfigurationTypeSet | undefined,
): string => {
  if (option != null) {
    return i18n.t(option.toString()) as string;
  }

  if (level === CleaningConfigurationLevel.Room || roomOption == null) {
    if (globalOption) {
      return `${i18n.t("inherited-from-global")} (${i18n.t(globalOption.toString()) as string})`;
    }
  } else if (roomOption) {
    return `${i18n.t("inherited-from-room")} (${i18n.t(roomOption.toString()) as string})`;
  }
  return i18n.t("unknown-value") as string;
};

export const getCleaningToggleOptionText = (
  option: boolean | null,
  level: CleaningConfigurationLevel,
  roomOption: boolean | undefined | null,
  globalOption: boolean | undefined,
): string => {
  if (option != null) {
    return i18n.t(`cleaning-configuration-switch-${option ? "on" : "off"}`) as string;
  }

  if (level === CleaningConfigurationLevel.Room || roomOption == null) {
    if (globalOption != null) {
      const infoString = i18n.t(`cleaning-configuration-switch-${globalOption ? "on" : "off"}`) as string;
      return `${i18n.t("inherited-from-global")} (${infoString})`;
    }
  } else if (roomOption != null) {
    const infoString = i18n.t(`cleaning-configuration-switch-${roomOption ? "on" : "off"}`) as string;
    return `${i18n.t("inherited-from-room")} (${infoString})`;
  }
  return i18n.t("unknown-value") as string;
};

export const getFrontLightsOptionText = (
  option: FrontLightsMode | null,
  level: CleaningConfigurationLevel,
  roomOption: FrontLightsMode | undefined | null,
  globalOption: FrontLightsMode | undefined,
): string => {
  if (option != null) {
    return i18n.t(`cleaning-configuration-front-lights-mode-${option}`) as string;
  }

  if (level === CleaningConfigurationLevel.Room || roomOption == null) {
    if (globalOption != null) {
      const infoString = i18n.t(`cleaning-configuration-front-lights-mode-${globalOption}`) as string;
      return `${i18n.t("inherited-from-global")} (${infoString})`;
    }
  } else if (roomOption != null) {
    const infoString = i18n.t(`cleaning-configuration-front-lights-mode-${roomOption}`) as string;
    return `${i18n.t("inherited-from-room")} (${infoString})`;
  }
  return i18n.t("unknown-value") as string;
};

export const getCleaningSetting = (
  configurations: EnvironmentPollution[] | IEnvironmentHazard[] | ISpatialProperty[] | null,
  option: EnvironmentPollutionType | EnvironmentHazardType | SpatialPropertyType,
  level: CleaningConfigurationLevel,
  roomOption: EnvironmentPollutionType | EnvironmentHazardType | SpatialPropertyType | undefined | null,
  globalOption: EnvironmentPollutionType | EnvironmentHazardType | SpatialPropertyType | undefined,
) => {
  if (configurations === null || configurations.length === 0) {
    return null;
  }

  if (option == null) {
    if (level === CleaningConfigurationLevel.Room || roomOption == null) {
      if (globalOption) {
        option = globalOption;
      }
    } else if (roomOption) {
      option = roomOption;
    }
  }

  if (isEnvironmentPollution(configurations[0])) {
    return (configurations as EnvironmentPollution[]).find((c) => c.type === option) ?? null;
  } else if (isIEnvironmentHazard(configurations[0])) {
    return (configurations as IEnvironmentHazard[]).find((c) => c.type === option) ?? null;
  } else if (isISpatialProperty(configurations[0])) {
    return (configurations as ISpatialProperty[]).find((c) => c.type === option) ?? null;
  }

  return null;
};

export const isEnvironmentHazardType = (
  val: EnvironmentPollutionType | EnvironmentHazardType | SpatialPropertyType,
): val is EnvironmentHazardType => {
  return Object.values(EnvironmentHazardType).includes(val as EnvironmentHazardType);
};

export const isEnvironmentPollution = (
  config: EnvironmentPollution | IEnvironmentHazard | ISpatialProperty,
): config is EnvironmentPollution => {
  return (
    "frontBrushSpeed" in config &&
    "rollerBrushSpeed" in config &&
    "aspirationSpeed" in config &&
    "cleaningDrivingSpeed" in config &&
    "brushesBwPeriod" in config &&
    "lidarSensitivity" in config &&
    "cameraSensitivity" in config
  );
};

export const isIEnvironmentHazard = (
  config: EnvironmentPollution | IEnvironmentHazard | ISpatialProperty,
): config is IEnvironmentHazard => {
  return (
    "allowUndockWithoutValidRoom" in config &&
    "driveBackwardsEnabled" in config &&
    "dropRescueEnabled" in config &&
    "positionLostRescueEnabled" in config
  );
};

export const isISpatialProperty = (
  config: EnvironmentPollution | IEnvironmentHazard | ISpatialProperty,
): config is ISpatialProperty => {
  return "localizationMode" in config && "useSlamLite" in config && "zoneExplorationEnabled" in config;
};

export const mapCleaningConfigurationApiToCleaningConfiguration = (apiData: CleaningConfigurationApi) =>
  ({
    spatialProperty: apiData.spatialProperty === INHERIT ? null : apiData.spatialProperty,
    environmentPollution: apiData.environmentPollution === INHERIT ? null : apiData.environmentPollution,
    environmentHazard: apiData.environmentHazard === INHERIT ? null : apiData.environmentHazard,
    independentLegacy2601: mapApiToIndependentLegacy(apiData.independentLegacy2601),
  }) as CleaningConfiguration;

export const mapCleaningConfigurationToApi = (cleaningConfiguration: CleaningConfiguration) =>
  ({
    spatialProperty: cleaningConfiguration.spatialProperty ?? INHERIT,
    environmentPollution: cleaningConfiguration.environmentPollution ?? INHERIT,
    environmentHazard: cleaningConfiguration.environmentHazard ?? INHERIT,
    independentLegacy2601: mapIndependentLegacyToApi(cleaningConfiguration.independentLegacy2601),
  }) as CleaningConfigurationApi;

export const mapIndependentLegacyToApi = (independent?: IndependentConfigurationLegacy2601 | null) =>
  (independent == null
    ? null
    : {
        beeper: independent.beeper ?? INHERIT,
        warningLight: independent.warningLight ?? INHERIT,
        minObstacleHeightM: independent.minObstacleHeightM ?? INHERIT,
      }) as IndependentConfigurationLegacy2601Api;

export const mapApiToIndependentLegacy = (apiData?: IndependentConfigurationLegacy2601Api | null) =>
  (apiData == null
    ? null
    : {
        beeper: apiData.beeper === INHERIT ? null : apiData.beeper,
        warningLight: apiData.warningLight === INHERIT ? null : apiData.warningLight,
        minObstacleHeightM: apiData.minObstacleHeightM === INHERIT ? null : apiData.minObstacleHeightM,
      }) as IndependentConfigurationLegacy2601;

export const mapPartialCleaningConfigurationToApi = (cleaningConfiguration: Partial<CleaningConfiguration>) =>
  ({
    spatialProperty:
      cleaningConfiguration.spatialProperty !== undefined
        ? cleaningConfiguration.spatialProperty === null
          ? INHERIT
          : cleaningConfiguration.spatialProperty
        : undefined,
    environmentPollution:
      cleaningConfiguration.environmentPollution !== undefined
        ? cleaningConfiguration.environmentPollution === null
          ? INHERIT
          : cleaningConfiguration.environmentPollution
        : undefined,
    environmentHazard:
      cleaningConfiguration.environmentHazard !== undefined
        ? cleaningConfiguration.environmentHazard === null
          ? INHERIT
          : cleaningConfiguration.environmentHazard
        : undefined,
    independentLegacy2601:
      cleaningConfiguration.independentLegacy2601 !== undefined
        ? mapIndependentLegacyToApi(cleaningConfiguration.independentLegacy2601)
        : undefined,
  }) as CleaningConfigurationApi;

export const mapPartialCleaningConfigurationApiToCleaningConfiguration = (apiData: Partial<CleaningConfigurationApi>) =>
  ({
    spatialProperty:
      apiData.spatialProperty == null
        ? undefined
        : apiData.spatialProperty === INHERIT
          ? null
          : apiData.spatialProperty,
    environmentPollution:
      apiData.environmentPollution == null
        ? undefined
        : apiData.environmentPollution === INHERIT
          ? null
          : apiData.environmentPollution,
    environmentHazard:
      apiData.environmentHazard == null
        ? undefined
        : apiData.environmentHazard === INHERIT
          ? null
          : apiData.environmentHazard,
    independentLegacy2601:
      apiData.independentLegacy2601 !== undefined
        ? mapApiToIndependentLegacy(apiData.independentLegacy2601)
        : undefined,
  }) as CleaningConfiguration;
